import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { EditIssueTeamDto, FetchError, GetIssuesDto, IssueDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";
import { useSignalR } from "../../../hooks";
import { optimisticallyUpdateQueryData } from "../utilities";

type Variables = {
  issueGuid: string;
  oldTeamSlug?: string;
  newTeamSlug?: string;
  newTeamName?: string;
  createdByUserId: string;
};

const useEditIssueTeamRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editIssueTeamRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditIssueTeamDto = {
      newTeamSlug: variables.newTeamSlug,
    };

    const response = await fetch(`${configuration.apiRootUrl}/issues/${variables.issueGuid}/team`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editIssueTeamRequest };
};

const useEditIssueTeam = () => {
  const { editIssueTeamRequest } = useEditIssueTeamRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editIssueTeamRequest,
    onMutate: async (variables) => {
      let cachedOldTeamIssuesData: GetIssuesDto | undefined;
      let cachedNewTeamIssuesData: GetIssuesDto | undefined;
      let cachedUserIssuesData: GetIssuesDto | undefined;
      let cachedIssueDetailsData: IssueDto | undefined;

      if (variables.oldTeamSlug && variables.oldTeamSlug !== variables.newTeamSlug) {
        cachedOldTeamIssuesData = await optimisticallyUpdateQueryData<GetIssuesDto>(
          queryClient,
          queryKeys.issues.getIssues(variables.oldTeamSlug, undefined),
          (cachedData) => ({
            ...cachedData,
            issues: cachedData.issues.filter((issue) => issue.guid !== variables.issueGuid),
          })
        );
      }

      if (variables.newTeamSlug) {
        cachedNewTeamIssuesData = await optimisticallyUpdateQueryData<GetIssuesDto>(
          queryClient,
          queryKeys.issues.getIssues(variables.newTeamSlug, undefined),
          (cachedData) => ({
            ...cachedData,
            issues: cachedData.issues.map((issue) =>
              issue.guid === variables.issueGuid
                ? {
                    ...issue,
                    assignedTeam:
                      variables.newTeamSlug && variables.newTeamName
                        ? { slug: variables.newTeamSlug, name: variables.newTeamName }
                        : undefined,
                  }
                : issue
            ),
          })
        );
      }

      cachedUserIssuesData = await optimisticallyUpdateQueryData<GetIssuesDto>(
        queryClient,
        queryKeys.issues.getIssues(undefined, variables.createdByUserId),
        (cachedData) => ({
          ...cachedData,
          issues: cachedData.issues.map((issue) =>
            issue.guid === variables.issueGuid
              ? {
                  ...issue,
                  assignedTeam:
                    variables.newTeamSlug && variables.newTeamName
                      ? { slug: variables.newTeamSlug, name: variables.newTeamName }
                      : undefined,
                }
              : issue
          ),
        })
      );

      cachedIssueDetailsData = await optimisticallyUpdateQueryData<IssueDto>(
        queryClient,
        queryKeys.issues.getIssueDetail(variables.issueGuid),
        (cachedData) => ({
          ...cachedData,
          assignedTeam:
            variables.newTeamSlug && variables.newTeamName
              ? { slug: variables.newTeamSlug, name: variables.newTeamName }
              : undefined,
        })
      );

      return { cachedOldTeamIssuesData, cachedNewTeamIssuesData, cachedUserIssuesData, cachedIssueDetailsData };
    },
    onError: (_error, variables, context) => {
      if (variables.oldTeamSlug && variables.oldTeamSlug !== variables.newTeamSlug) {
        queryClient.setQueryData(
          queryKeys.issues.getIssues(variables.oldTeamSlug, undefined),
          context?.cachedOldTeamIssuesData
        );
      }
      if (variables.newTeamSlug) {
        queryClient.setQueryData(
          queryKeys.issues.getIssues(variables.newTeamSlug, undefined),
          context?.cachedNewTeamIssuesData
        );
      }
      if (variables.createdByUserId) {
        queryClient.setQueryData(
          queryKeys.issues.getIssues(undefined, variables.createdByUserId),
          context?.cachedUserIssuesData
        );
      }
      queryClient.setQueryData(queryKeys.issues.getIssueDetail(variables.issueGuid), context?.cachedIssueDetailsData);
    },
    onSuccess: (_data, variables) => {
      if (variables.oldTeamSlug) {
        signalR.sendRefetchTeamIssues(variables.oldTeamSlug);
      }
      if (variables.newTeamSlug) {
        signalR.sendRefetchTeamIssues(variables.newTeamSlug);
      }
      if (variables.createdByUserId) {
        signalR.sendRefetchUserIssues(variables.createdByUserId);
      }
      signalR.sendRefetchIssue(variables.issueGuid);
      signalR.sendRefetchComments(variables.issueGuid);
    },
    onSettled: (_data, _error, variables) => {
      if (variables.oldTeamSlug) {
        queryClient.invalidateQueries(queryKeys.issues.getIssues(variables.oldTeamSlug, undefined));
      }
      if (variables.newTeamSlug) {
        queryClient.invalidateQueries(queryKeys.issues.getIssues(variables.newTeamSlug, undefined));
      }
      if (variables.createdByUserId) {
        queryClient.invalidateQueries(queryKeys.issues.getIssues(undefined, variables.createdByUserId));
      }
      queryClient.invalidateQueries(queryKeys.issues.getIssueDetail(variables.issueGuid));
      queryClient.invalidateQueries(queryKeys.issues.getIssueFeed(variables.issueGuid));
    },
  });
};

export { useEditIssueTeam };
